import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CreditCard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Loading } from "@/components/States";
import PlanEditor from "@/components/admin/PlanEditor";
import { api, formatError } from "@/lib/api";
import { inr } from "@/lib/plan";
import { Input } from "@/components/ui/input";
import { AdminCoupons, AdminPayments } from "@/components/admin/AdminCoupons";
import { AdminAiCosts } from "@/components/admin/AdminAiCosts";

function BillingPanel({ data, onSaved }) {
  const [notice, setNotice] = useState(data.notice);
  const [enabled, setEnabled] = useState(data.payments.enabled);
  const [trial, setTrial] = useState(data.trial_days);
  const pay = data.payments;
  const save = async () => {
    try { await api.put("/admin/billing", { notice, payments_enabled: enabled, trial_days: Number(trial) }); toast.success("Billing settings saved"); onSaved(); } catch (e) { toast.error(formatError(e)); setEnabled(pay.enabled); }
  };
  return (
    <div data-testid="admin-billing" className="grid gap-4 rounded-xl border border-line bg-surface p-6">
      <div className="flex flex-wrap items-center gap-3">
        <CreditCard className="h-5 w-5 text-aqua" /><h3 className="font-bold text-ink">Payment gateway: Razorpay</h3>
        <span data-testid="gateway-status" className={`rounded-full px-3 py-1 text-xs font-semibold ${pay.live ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-800"}`}>
          {pay.live ? "Live" : pay.configured ? "Configured · disabled" : "Not configured · disabled"}</span>
        {pay.configured && <span data-testid="gateway-mode" className={`rounded-full px-3 py-1 text-xs font-semibold ${pay.mode === "live" ? "bg-solid text-white" : "bg-elevated text-ink-2"}`}>{pay.mode === "live" ? "LIVE keys · real money" : "TEST keys · no real money"}</span>}
      </div>
      <p className="text-sm text-ink-2">{pay.live ? "Learners pay with Razorpay Standard Checkout. Plans activate only after the payment signature is verified." : "Upgrades only record a learner's interest until payments are switched on. No payment is simulated."} You can also assign plans by hand in the Users tab.</p>
      <p data-testid="webhook-status" className="text-xs text-ink-3">Webhook (/api/billing/webhook): {pay.webhook_configured ? "configured" : "not configured (optional, set RAZORPAY_WEBHOOK_SECRET)"}</p>
      <label className="flex items-center gap-3 text-sm font-medium"><Switch data-testid="payments-enabled-switch" checked={enabled} disabled={!pay.configured} onCheckedChange={setEnabled} />Accept online payments</label>
      <div className="flex max-w-xs flex-col gap-1.5"><Label className="text-xs">Free Plus trial for new signups (days, 0 = off)</Label><Input type="number" min={0} max={60} data-testid="trial-days-input" value={trial} onChange={(e) => setTrial(e.target.value)} /></div>
      <div className="flex flex-col gap-1.5"><Label className="text-xs">Notice shown on the pricing page</Label><Textarea data-testid="pricing-notice-input" rows={2} value={notice} onChange={(e) => setNotice(e.target.value)} /></div>
      <div className="flex flex-wrap gap-3">
        <Button data-testid="billing-save-btn" onClick={save} className="rounded-full">Save billing settings</Button>
        <Button data-testid="run-reminders-btn" variant="outline" className="rounded-full" onClick={async () => { try { const r = (await api.post("/admin/plan-reminders/run")).data; toast.success(`Expiry reminders sent: ${r.sent}${r.failed ? `, failed: ${r.failed}` : ""}`); } catch (e) { toast.error(formatError(e)); } }}>Send expiry reminders now</Button>
      </div>
    </div>
  );
}

function UpgradeRequests() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/admin/upgrade-requests").then((r) => setRows(r.data)); }, []);
  if (!rows) return <Loading />;
  return (
    <div data-testid="admin-upgrade-requests" className="overflow-x-auto rounded-xl border border-line bg-surface">
      <h3 className="p-4 font-bold text-ink">Upgrade interest ({rows.length})</h3>
      {rows.length === 0 ? <p className="px-4 pb-4 text-sm text-ink-3">No upgrade requests yet.</p> : (
        <table className="w-full text-sm">
          <thead className="bg-canvas-2 text-left text-xs uppercase tracking-wider text-ink-3"><tr><th className="p-3">Learner</th><th className="p-3">Plan</th><th className="p-3">Cycle</th><th className="p-3">Amount</th><th className="p-3">Status</th><th className="p-3">Updated</th></tr></thead>
          <tbody>{rows.map((r) => (
            <tr key={r.id} data-testid={`upgrade-request-${r.id}`} className="border-t border-line">
              <td className="p-3">{r.name}<p className="text-xs text-ink-3">{r.email}</p></td><td className="p-3 capitalize">{r.plan_id}</td><td className="p-3 capitalize">{r.cycle}</td>
              <td className="p-3 font-mono">{inr(r.amount)}</td><td className="p-3 text-xs">{r.status === "fulfilled_by_admin" ? "Assigned by admin" : "Awaiting payments"}</td><td className="p-3 text-xs">{new Date(r.updated_at).toLocaleDateString()}</td>
            </tr>))}</tbody>
        </table>
      )}
    </div>
  );
}

export default function AdminPlans() {
  const [data, setData] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [v, setV] = useState(0);
  const load = () => api.get("/admin/plans").then((r) => { setData(r.data); setV((x) => x + 1); });
  useEffect(() => { load(); api.get("/plans").then((r) => setSubjects(r.data.subjects)); }, []);
  if (!data) return <Loading />;
  return (
    <div data-testid="admin-plans" className="grid gap-6">
      <BillingPanel key={`b${v}`} data={data} onSaved={load} />
      {data.plans.map((p) => <PlanEditor key={`${p.id}${v}`} plan={p} subjects={subjects} subscribers={data.subscribers[p.id]} onSaved={load} />)}
      <AdminAiCosts key={`a${v}`} data={data} onSaved={load} />
      <AdminCoupons />
      <AdminPayments />
      <UpgradeRequests />
    </div>
  );
}
